import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useCurrentUser } from '../../hooks/useCurrentUser'
import useApi from '../../hooks/useApi'
import { ensureAbsoluteUrl } from '../../utils/url'
import useCommunityMapPins from '../../hooks/useCommunityMapPins'
import useFollowState from '../../hooks/useFollowState'
import useProfileMapCenter from '../../hooks/useProfileMapCenter'
import useProfileView from '../../hooks/useProfileView'
import ProfileTemplate from './ProfileTemplate'
import EventSlideshowModal from '../../components/EventSlideshowModal'
import { ProfileMain } from '../../components/Profiles'
import { UserProfileLeftSidebar } from '../../components/Profiles/UserProfile'
import Loading from '../../components/loading'


function buildUserProfileSubtitle(user, t) {
  if (!user) return null
  const nickLine =
    user.nickname != null && String(user.nickname).trim() !== ''
      ? `@${user.nickname}`
      : null
  const titleTrimmed = (user.work_title ?? '').trim()
  const retired = !!user.retired
  let jobLine = null
  if (titleTrimmed && retired) {
    jobLine = `${titleTrimmed} ${t('profiles.retiredAfterTitle', '(retired)')}`
  } else if (titleTrimmed) {
    jobLine = titleTrimmed
  } else if (retired) {
    jobLine = t('profiles.retiredStandalone', 'Retired')
  }
  const parts = [nickLine, jobLine].filter(Boolean)
  if (parts.length === 0) return null
  return parts.join('\n')
}

function buildSocialLinksFromUser(obj) {
  if (!obj) return []
  const platforms = [
    { key: 'facebook_link', platform: 'facebook' },
    { key: 'instagram_link', platform: 'instagram' },
    { key: 'linkedin_link', platform: 'linkedin' },
    { key: 'youtube_link', platform: 'youtube' },
    { key: 'tiktok_link', platform: 'tiktok' },
  ]
  return platforms
    .filter(({ key }) => obj[key])
    .map(({ platform, key }) => ({ platform, href: ensureAbsoluteUrl(obj[key]), label: platform }))
}

export default function UserProfilePage() {
  const { t, i18n } = useTranslation()
  const { username: usernameParam } = useParams()
  const { currentUser } = useCurrentUser()
  const { userFollow, userUnfollow } = useApi()

  const {
    user,
    setUser,
    groups,
    upcomingEvents,
    followersCount,
    isOwnProfile,
    isLoading,
    notFound,
  } = useProfileView({ usernameParam, currentUser })

  const { isFollowing, followLoading, onFollowClick } = useFollowState({
    subjectId: !isOwnProfile ? user?.id : null,
    initialFollowing: user?.current_user_follows ?? false,
    followApi: userFollow,
    unfollowApi: userUnfollow,
    onSuccess: useCallback(
      nextFollowing =>
        setUser(prev => (prev ? { ...prev, current_user_follows: nextFollowing } : prev)),
      [setUser],
    ),
  })

  const displayUser = user ?? currentUser
  const locationStr = displayUser?.location ?? null
  const profileUserId = displayUser?.id ?? null
  const { center: mapCenter, loading: mapCenterLoading } = useProfileMapCenter({
    location: locationStr,
    enabled: !!locationStr,
  })
  const { pins: mapPins } = useCommunityMapPins('user', profileUserId)
  const [slideshowEvent, setSlideshowEvent] = React.useState(null)

  const profileUrl =
    displayUser?.nickname != null
      ? `${window.location.origin}/profile/${encodeURIComponent(displayUser.nickname)}`
      : null
  const onShareClick = useCallback(() => {
    if (!profileUrl) return
    navigator.clipboard.writeText(profileUrl).then(
      () => {
        if (typeof window !== 'undefined' && window.alert) {
          window.alert(t('share.copied', 'Profile link copied to clipboard.'))
        }
      },
      () => {},
    )
  }, [profileUrl, t])

  const handleOpenSlideshow = useCallback(
    ({ eventId, eventSlug }) => {
      setSlideshowEvent({
        id: eventId,
        slug: eventSlug || null,
      })
    },
    [],
  )

  const handleCloseSlideshow = useCallback(() => {
    setSlideshowEvent(null)
  }, [])

  if (isLoading) {
    return <Loading show />
  }

  if (notFound || (!user && !isOwnProfile)) {
    return (
      <div className="container py-5">
        <p className="text-muted">{t('profiles.userNotFound', 'User not found.')}</p>
      </div>
    )
  }

  const avatarUrl =
    displayUser?.media_attachment?.thumbnail_url ??
    displayUser?.media_attachment?.direct_upload_url ??
    '/images/default-user.png'
  const subtitle = buildUserProfileSubtitle(displayUser, t)
  const coverImageUrl =
    displayUser?.cover_photo?.thumbnail_url ??
    displayUser?.cover_photo?.direct_upload_url ??
    undefined
  const socialLinks = buildSocialLinksFromUser(displayUser)
  const profileHref = profileUrl ?? '/profile'

  return (
    <>
      <ProfileTemplate
        profileType="user"
        headerProps={{
          name: displayUser?.name ?? null,
          avatarUrl,
          subtitle,
          coverImageUrl,
          onCoverPhotosClick: isOwnProfile
            ? () => {
                // TODO: open cover photo modal or navigate to settings when flow is ready
              }
            : undefined,
          isFollowing,
          onFollowClick: !isOwnProfile && displayUser?.id ? onFollowClick : undefined,
          followLoading,
          onShareClick: profileUrl ? onShareClick : undefined,
          socialLinks,
        }}
        breadcrumbItems={[
          { label: t('profiles.profile', 'Profile'), href: profileHref },
          { label: displayUser?.name ?? t('profiles.you', 'You') },
        ]}
        leftSidebarContent={
          <UserProfileLeftSidebar
            name={displayUser?.name}
            location={displayUser?.location}
            quote={displayUser?.quote ?? displayUser?.user_quote ?? displayUser?.favorite_quote}
            quoteAuthor={displayUser?.quote_author ?? displayUser?.favorite_quote_author}
            followersCount={followersCount}
            onEditCommunity={undefined}
            favoriteCharity={displayUser?.favorite_charity}
            categories={displayUser?.categories}
            groups={groups}
            upcomingEvents={upcomingEvents}
            awards={[]}
            locale={displayUser?.language ?? i18n?.language ?? 'en'}
          />
        }
        mainContent={
          <ProfileMain
            location={displayUser?.location}
            mapCenter={mapCenter}
            mapCenterLoading={mapCenterLoading}
            profileType="user"
            mapPins={mapPins}
            onOpenSlideshow={handleOpenSlideshow}
          />
        }
        rightSidebarContent={null}
      />
      {slideshowEvent && (
        <EventSlideshowModal
          show
          onHide={handleCloseSlideshow}
          eventId={slideshowEvent.id}
          eventSlug={slideshowEvent.slug}
        />
      )}
    </>
  )
}
